// Tour definitions. Each step:
//   target:      CSS selector | ref | element. null = centered modal.
//   placement:   'top' | 'bottom' | 'left' | 'right' | 'auto' (default 'auto')
//   title:       short headline
//   description: 1–3 sentences explaining what the element is / what to do
//   finishLabel: optional label for the final step's primary button

export const HOME_TOUR_STEPS = [
  {
    id: 'welcome',
    target: null,
    title: 'Welcome to NOVAS 👋',
    description: 'A quick 60-second tour so you know where everything lives. You can replay this any time using the Quick Tour button in the bottom-right corner.',
  },
  {
    id: 'stats-students',
    target: '[data-tour="stat-students"]',
    placement: 'bottom',
    title: 'Active students',
    description: 'Total number of students you currently teach.',
  },
  {
    id: 'stats-week',
    target: '[data-tour="stat-lessons-week"]',
    placement: 'bottom',
    title: 'Lessons this week',
    description: 'How many lessons you have already taught this week — based on your Schedule.',
  },
  {
    id: 'stats-month',
    target: '[data-tour="stat-lessons-month"]',
    placement: 'bottom',
    title: 'Lessons this month',
    description: 'Monthly lesson count. Useful for invoicing or checking your pace against personal goals.',
  },
  {
    id: 'stats-money',
    target: '[data-tour="stat-money"]',
    placement: 'bottom',
    title: 'Money this month',
    description: 'Your earnings this month, based on completed lessons and the price set on each student profile.',
  },
  {
    id: 'nav-students',
    target: '[data-tour="nav-students"]',
    placement: 'right',
    title: 'Students',
    description: 'Manage all your students here — add new ones, see active and inactive students, and open any profile to view homework, lessons and notes.',
  },
  {
    id: 'nav-schedule',
    target: '[data-tour="nav-schedule"]',
    placement: 'right',
    title: 'Schedule',
    description: 'Your weekly calendar. Plan lessons, mark them as done, and see upcoming sessions across all students.',
  },
  {
    id: 'nav-profile',
    target: '[data-tour="nav-profile"]',
    placement: 'right',
    title: 'Profile Settings',
    description: 'Click your avatar at the bottom of the sidebar to open Profile Settings — upload your photo, set your timezone (important for the schedule!), and manage your account.',
  },
  {
    id: 'finish',
    target: null,
    title: "You're ready 🎉",
    description: "Next step: add your first student. We'll start a short tour as soon as the profile opens, showing what each field does.",
    finishLabel: 'Add my first student',
  },
];

// ─── Student profile tour ──────────────────────────────────────────────────
export const STUDENT_TOUR_STEPS = [
  {
    id: 'welcome',
    target: null,
    title: "Let's set up your first student 🧑‍🎓",
    description: "This profile is the heart of every lesson. The more you fill in here, the better Smart Lesson generation and AI Recommendations will be.",
  },
  {
    id: 'name',
    target: '[data-tour="student-name"]',
    placement: 'right',
    title: 'Name',
    description: 'Start with the student\'s name. It appears on the schedule, lesson banners, and reports.',
  },
  {
    id: 'proficiency',
    target: '[data-tour="student-proficiency"]',
    placement: 'right',
    title: 'Proficiency level',
    description: 'CEFR level (A1 – C2). Smart Lesson generation matches reading length, vocab and grammar to this level.',
  },
  {
    id: 'dob',
    target: '[data-tour="student-dob"]',
    placement: 'right',
    title: 'Date of birth',
    description: 'Type dd/mm/yyyy directly or use the calendar icon. Used for age-appropriate topics and the Schedule.',
  },
  {
    id: 'price',
    target: '[data-tour="student-price"]',
    placement: 'right',
    title: 'Price',
    description: 'How much they pay per lesson. Drives the "Money this month" and "Planned income" stats on Home.',
  },
  {
    id: 'status',
    target: '[data-tour="student-status"]',
    placement: 'right',
    title: 'Status',
    description: 'Active / On Vacation / Sick Leave / Paused. When status is anything but Active, pick the date range — that period is excluded from your schedule.',
  },
  {
    id: 'profile-fields',
    target: '[data-tour="student-profile-column"]',
    placement: 'left',
    title: 'Profile details',
    description: 'Experience, Goals, Preferences, Hobbies, Strengths/Weaknesses. Each field has hints inside showing what to write — these power personalised lesson topics and AI suggestions.',
  },
  {
    id: 'ai-recs',
    target: '[data-tour="student-ai-recs"]',
    placement: 'left',
    title: 'AI Recommendations',
    description: 'Once the profile fields are filled, click "Generate AI recommendations" — the AI reads your notes and produces personalised teaching strategies for THIS student.',
  },
  {
    id: 'contact',
    target: '[data-tour="student-contact"]',
    placement: 'left',
    title: 'Contact info',
    description: 'Email, phone, parent contact, social links — anything you need to reach the student or their family.',
    note: 'Important: the student will only be able to log into their profile using the email you enter here. Double-check it before saving.',
  },
  {
    id: 'save',
    target: '[data-tour="student-save"]',
    placement: 'top',
    title: '⚠️ Save the student!',
    description: "Don't forget to click Save Student — nothing is saved automatically. You can come back any time to edit, but the profile must exist first before you can schedule lessons or use Smart Lesson.",
    finishLabel: 'Got it!',
  },
];

// ─── Lesson workspace tour ─────────────────────────────────────────────────
export const LESSON_TOUR_STEPS = [
  {
    id: 'welcome',
    target: null,
    title: 'Your lesson workspace ✨',
    description: "Build a lesson by combining text, exercises, whiteboards and AI-generated content. Don't worry — Save Lesson at the top keeps everything safe.",
  },
  {
    id: 'title',
    target: '[data-tour="lesson-title"]',
    placement: 'bottom',
    title: 'Lesson title',
    description: 'Click to rename. The title appears on the schedule and in the student\'s lesson list.',
  },
  {
    id: 'editor',
    target: '[data-tour="lesson-editor"]',
    placement: 'top',
    title: 'Main editor',
    description: 'This is where you type your lesson. Bold, lists, headings, links, tables, images, audio, video, PDFs — and you can also insert ready-made exercises (fill in the gaps, true/false, matching, etc.) directly into the text.',
  },
  {
    id: 'add-elements',
    target: '[data-tour="lesson-add-elements"]',
    placement: 'left',
    title: 'Add elements (+)',
    description: 'The "+" circle opens a menu where you insert content blocks into the editor: images, video, audio, PDF, materials from your library, a Smart Lesson generated by AI, and more.',
  },
  {
    id: 'add-exercises',
    target: '[data-tour="lesson-add-exercises"]',
    placement: 'left',
    title: 'AI menu (★)',
    description: 'Click the star circle to open the AI menu — three tools live inside: Ask Me, Generate an exercise, and Generate Smart Lesson. Let me show you each one.',
    setup: () => {
      const star = document.querySelector('[data-tour="lesson-add-exercises"] button');
      if (star) star.click();
    },
  },
  {
    id: 'ai-askme',
    target: '[data-tour="lesson-ai-askme"]',
    placement: 'left',
    title: 'Ask Me — chat assistant',
    description: 'A quick ChatGPT-style chat. Ask the AI to explain a grammar rule, suggest a topic for a B2 student, write a model answer — and paste the response straight into the editor.',
    setup: () => {
      // Re-open the popup if it was closed and reset the inner state to the menu
      const star = document.querySelector('[data-tour="lesson-add-exercises"] button');
      if (star && !document.querySelector('[data-tour="lesson-ai-askme"]')) star.click();
    },
  },
  {
    id: 'ai-exercise',
    target: '[data-tour="lesson-ai-exercise"]',
    placement: 'left',
    title: 'Generate an exercise',
    description: 'Pick an exercise type (fill in the gaps, true/false, multiple choice, matching, word order, error correction, dialogue, vocabulary, and more) and AI generates it on your topic. Insert it into the editor or onto the whiteboard.',
    setup: () => {
      const star = document.querySelector('[data-tour="lesson-add-exercises"] button');
      if (star && !document.querySelector('[data-tour="lesson-ai-exercise"]')) star.click();
    },
  },
  {
    id: 'ai-smart',
    target: '[data-tour="lesson-ai-smart"]',
    placement: 'left',
    title: 'Generate Smart Lesson',
    description: 'The big one. Drop in a URL, paste an article, or just describe a topic — AI builds a full lesson: reading, vocabulary, grammar, 9 exercises (across reading/vocab/grammar) and a relevant image, all in 30 seconds.',
    setup: () => {
      const star = document.querySelector('[data-tour="lesson-add-exercises"] button');
      if (star && !document.querySelector('[data-tour="lesson-ai-smart"]')) star.click();
    },
  },
  {
    id: 'add-tab',
    target: '[data-tour="lesson-add-tab"]',
    placement: 'top',
    title: 'Add a tab (+)',
    description: 'The "+" at the bottom centre adds another tab to this lesson: a whiteboard (for drawing / visual exercises), another editor page (e.g. for grammar references), or a homework page the student fills in later.',
  },
  {
    id: 'reactions',
    target: '[data-tour="lesson-reactions"]',
    placement: 'left',
    title: 'Reactions',
    description: 'Quick emoji reactions during a live lesson — clap, thumbs up, heart, party. The student sees them float across their screen in real time. Great for encouragement without breaking the flow.',
  },
  {
    id: 'chat',
    target: '[data-tour="lesson-chat"]',
    placement: 'left',
    title: 'Chat with the student',
    description: 'Text chat with this student. Useful for quick notes between lessons, sharing a link, or chatting on mute during a call.',
    setup: () => {
      const btn = document.querySelector('[data-tour="lesson-chat"]');
      if (btn && !document.querySelector('[data-tour="chat-telegram"]')) btn.click();
    },
  },
  {
    id: 'chat-telegram',
    target: '[data-tour="chat-telegram"]',
    placement: 'bottom',
    title: 'Connect Telegram 💬',
    description: 'Click the Telegram badge to link this chat with your or the student\'s Telegram. Once linked, messages you send here are mirrored to Telegram — the student gets a notification on their phone even when NOVAS is closed.',
    setup: () => {
      // Make sure the chat modal stays open while we point at the Telegram badge
      const chatBtn = document.querySelector('[data-tour="lesson-chat"]');
      if (chatBtn && !document.querySelector('[data-tour="chat-telegram"]')) chatBtn.click();
    },
  },
  {
    id: 'dictionary',
    target: '[data-tour="lesson-dictionary"]',
    placement: 'left',
    title: 'Dictionary',
    description: 'Open the dictionary to look up words and add them to the student\'s personal vocab list. The student also sees these words on their dashboard for review.',
  },
  {
    id: 'start-lesson',
    target: '[data-tour="lesson-start"]',
    placement: 'left',
    title: 'Start the lesson',
    description: 'When the student joins, click this to begin a live video call. Cursors, exercise answers, and reactions sync in real time.',
  },
  {
    id: 'save',
    target: '[data-tour="lesson-save"]',
    placement: 'bottom',
    title: '⚠️ Save your work',
    description: 'Click Save Lesson regularly. The lesson is also linked to a student profile and a schedule slot, so it appears on their dashboard.',
    finishLabel: 'Start teaching!',
  },
];

// ─── Student dashboard tour ────────────────────────────────────────────────
export const STUDENT_DASHBOARD_TOUR_STEPS = [
  {
    id: 'welcome',
    target: null,
    title: 'Welcome to NOVAS 🚀',
    description: "Quick 30-second tour to show you around your learning hub. You can replay it any time via the Tour button in the bottom-right corner.",
  },
  {
    id: 'banner',
    target: '[data-tour="student-home-banner"]',
    placement: 'bottom',
    title: 'Your profile banner',
    description: 'Your name and current proficiency level (your teacher updates this as you progress).',
  },
  {
    id: 'completed',
    target: '[data-tour="student-home-stat-completed"]',
    placement: 'bottom',
    title: 'Completed lessons',
    description: 'How many lessons you have finished out of the total your teacher assigned. Click a lesson below to keep going.',
  },
  {
    id: 'pay',
    target: '[data-tour="student-home-pay"]',
    placement: 'bottom',
    title: 'Pay tuition',
    description: 'Pay your teacher in a few taps once payments are live. Right now this button shows an "under development" notice — your teacher will let you know when it goes live.',
  },
  {
    id: 'teacher',
    target: '[data-tour="student-home-teacher"]',
    placement: 'right',
    title: 'Your teacher',
    description: "Your teacher's name and contact details — email, phone, anything else they shared. Reach out any time with questions about a lesson.",
  },
  {
    id: 'homework-widget',
    target: '[data-tour="student-home-homework-widget"]',
    placement: 'left',
    title: 'Homework at a glance',
    description: 'Quick summary of your assignments — how many are new and how many are waiting for review. Click anywhere on the card to jump straight to Homework.',
  },
  {
    id: 'dictionary-widget',
    target: '[data-tour="student-home-dictionary-widget"]',
    placement: 'left',
    title: 'Flashcards — last added words',
    description: 'A mini deck of the most recently saved words. Tap a card to flip it and reveal the meaning, tap the speaker to hear the pronunciation. Click "View all" to open the full Dictionary.',
  },
  {
    id: 'schedule',
    target: '[data-tour="student-home-schedule"]',
    placement: 'top',
    title: 'Class schedule',
    description: 'This week at a glance — days with a class are marked, today is highlighted. Click a day to see the lesson scheduled for it (time and topic).',
  },
  {
    id: 'lessons',
    target: '[data-tour="student-home-lessons"]',
    placement: 'top',
    title: 'Your lessons',
    description: 'All your lessons live here. Click any title to open it. The green "Enter class" button activates 10 minutes before the scheduled time — that\'s when your live session is ready.',
  },
  {
    id: 'nav-dictionary',
    target: '[data-tour="student-nav-dictionary"]',
    placement: 'right',
    title: 'Dictionary',
    description: 'Every word your teacher (or you) added during lessons lives here. Practise with flashcards, review meanings, and grow your personal vocabulary list.',
  },
  {
    id: 'nav-homework',
    target: '[data-tour="student-nav-homework"]',
    placement: 'right',
    title: 'Homework',
    description: 'Assignments from your teacher appear here. A red badge on the icon shows new homework you have not opened yet.',
  },
  {
    id: 'nav-profile',
    target: '[data-tour="student-nav-profile"]',
    placement: 'right',
    title: 'Profile settings',
    description: 'Click your avatar to set your photo, timezone (very important — your schedule depends on it!), and sign out.',
  },
  {
    id: 'finish',
    target: null,
    title: "You're ready 🎉",
    description: "That's it! Open your first lesson from the list below and dive in. Good luck with your studies!",
    finishLabel: 'Start learning',
  },
];
